import {
  Button,
  Icons,
  Separator,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import { PiCaretLeft, PiCaretRight } from 'react-icons/pi';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface PeriodNavigationActionsProps {
  onNext: () => Promise<void> | void;
  onPrevious: () => Promise<void> | void;
  isLoading: boolean;
  isPreviousDisabled: boolean;
  isNextDisabled: boolean;
  previousTooltip: string;
  nextTooltip: string;
  shouldShow: boolean;
  setDisableDragNnDropTooltip: React.Dispatch<React.SetStateAction<boolean>>;
  isDragging: boolean;
  variant?: 'external' | 'inline';
}

const SPINNER_SAFETY_TIMEOUT_MS = 10_000;

export const PeriodNavigationActions = React.memo(
  ({
    onNext,
    onPrevious,
    isLoading,
    isPreviousDisabled,
    isNextDisabled,
    previousTooltip,
    nextTooltip,
    shouldShow,
    setDisableDragNnDropTooltip,
    isDragging,
    variant = 'external',
  }: PeriodNavigationActionsProps) => {
    const [position, setPosition] = useState({ top: 0, left: 0 });
    const triggerRef = useRef<HTMLDivElement>(null);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const rafRef = useRef<number | null>(null);
    const observerRef = useRef<IntersectionObserver | null>(null);

    // Spinner direction is entirely owned by this component.
    // It's set on click and cleared when isLoading goes false.
    const [loadingDirection, setLoadingDirection] = useState<
      'next' | 'previous' | null
    >(null);

    const updatePosition = useCallback(() => {
      if (variant !== 'external' || !triggerRef.current) return;

      const rect = triggerRef.current.getBoundingClientRect();
      const newPosition = {
        top: rect.top + rect.height / 2 - 20,
        left: rect.left + 42,
      };

      setPosition((prev) => {
        const threshold = 1;
        const topChanged = Math.abs(prev.top - newPosition.top) > threshold;
        const leftChanged = Math.abs(prev.left - newPosition.left) > threshold;

        if (topChanged || leftChanged) {
          return newPosition;
        }
        return prev;
      });
    }, [variant]);

    // Use IntersectionObserver to detect when trigger element moves
    useEffect(() => {
      if (variant !== 'external' || !triggerRef.current) return;

      // Initial position
      updatePosition();

      // Create IntersectionObserver to detect position changes
      observerRef.current = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            // When the element's position changes (even slightly), update
            if (entry.isIntersecting) {
              if (rafRef.current) {
                cancelAnimationFrame(rafRef.current);
              }
              rafRef.current = requestAnimationFrame(() => {
                updatePosition();
              });
            }
          });
        },
        {
          threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1],
          root: null, // viewport
        },
      );

      observerRef.current.observe(triggerRef.current);

      return () => {
        if (observerRef.current) {
          observerRef.current.disconnect();
        }
        if (rafRef.current) {
          cancelAnimationFrame(rafRef.current);
        }
      };
    }, [updatePosition, variant]);

    // Additional MutationObserver to detect DOM changes in the parent container
    useEffect(() => {
      if (variant !== 'external' || !triggerRef.current) return;

      // Find the draggable container (the parent with drag-drop items)
      let container = triggerRef.current.parentElement;
      while (container && !container.hasAttribute('data-rfd-droppable-id')) {
        container = container.parentElement;
      }

      if (!container) return;

      const mutationObserver = new MutationObserver(() => {
        // When DOM changes (like reordering), update position after a delay
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
          if (rafRef.current) {
            cancelAnimationFrame(rafRef.current);
          }
          rafRef.current = requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              updatePosition();
            });
          });
        }, 100);
      });

      mutationObserver.observe(container, {
        childList: true,
        subtree: true,
      });

      return () => {
        mutationObserver.disconnect();
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    }, [updatePosition, variant]);

    // Handle this layer's drag state changes
    useEffect(() => {
      if (variant !== 'external') return;

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      if (isDragging) {
        // This layer is being dragged - no need to update (it's inline)
        return;
      }

      // This layer stopped being dragged - update position
      timeoutRef.current = setTimeout(() => {
        if (rafRef.current) {
          cancelAnimationFrame(rafRef.current);
        }
        rafRef.current = requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            updatePosition();
          });
        });
      }, 150);

      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    }, [isDragging, updatePosition, variant]);

    // Handle scroll events
    useEffect(() => {
      if (variant !== 'external' || !triggerRef.current) return;

      let scrollContainer = triggerRef.current.parentElement;
      while (
        scrollContainer &&
        !scrollContainer.classList.contains('overflow-auto')
      ) {
        scrollContainer = scrollContainer.parentElement;
      }

      if (!scrollContainer) return;

      const handleScroll = () => {
        if (rafRef.current) {
          cancelAnimationFrame(rafRef.current);
        }

        rafRef.current = requestAnimationFrame(() => {
          updatePosition();
        });
      };

      scrollContainer.addEventListener('scroll', handleScroll, {
        passive: true,
      });

      return () => {
        scrollContainer?.removeEventListener('scroll', handleScroll);
        if (rafRef.current) {
          cancelAnimationFrame(rafRef.current);
        }
      };
    }, [updatePosition, variant]);

    const handleNext = useCallback(() => {
      setLoadingDirection('next');
      onNext();
    }, [onNext]);

    const handlePrevious = useCallback(() => {
      setLoadingDirection('previous');
      onPrevious();
    }, [onPrevious]);

    // Clear spinner when isLoading settles to false.
    // Uses a short delay (300ms) to handle the case where isLoading
    // hasn't gone true yet (React may batch true→false in one frame).
    useEffect(() => {
      if (loadingDirection === null) return;

      if (!isLoading) {
        const timer = setTimeout(() => setLoadingDirection(null), 300);
        return () => clearTimeout(timer);
      }

      // isLoading is true — keep showing spinner, set safety timeout
      const safety = setTimeout(
        () => setLoadingDirection(null),
        SPINNER_SAFETY_TIMEOUT_MS,
      );
      return () => clearTimeout(safety);
    }, [loadingDirection, isLoading]);

    useEffect(() => {
      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        if (rafRef.current) {
          cancelAnimationFrame(rafRef.current);
        }
        if (observerRef.current) {
          observerRef.current.disconnect();
        }
      };
    }, []);

    const isPreviousLoading = loadingDirection === 'previous';
    const isNextLoading = loadingDirection === 'next';

    if (!shouldShow) {
      return null;
    }

    const controlsContent = (
      <div className="flex items-center bg-background space-x-0.5 rounded-md shadow-md border">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={handlePrevious}
              disabled={isPreviousDisabled || isLoading}
            >
              {!isPreviousLoading ? (
                <PiCaretLeft className="size-4" />
              ) : (
                <Icons.spinner className="size-4 animate-spin" />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent className="whitespace-pre-line">
            {previousTooltip}
          </TooltipContent>
        </Tooltip>
        <Separator orientation="vertical" className="h-4/6" />
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleNext}
              disabled={isNextDisabled || isLoading}
            >
              {!isNextLoading ? (
                <PiCaretRight className="size-4" />
              ) : (
                <Icons.spinner className="size-4 animate-spin" />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent className="whitespace-pre-line">
            {nextTooltip}
          </TooltipContent>
        </Tooltip>
      </div>
    );

    if (variant === 'inline') {
      if (!isDragging) {
        return null;
      }

      return (
        <div className="absolute -right-28 w-28 h-10 flex items-center justify-center">
          {controlsContent}
        </div>
      );
    }

    if (isDragging) {
      return <div ref={triggerRef} className="absolute -right-28 w-28 h-10" />;
    }

    const portalContent = (
      <div
        className="fixed flex space-x-1 z-50"
        style={{
          top: position.top,
          left: position.left,
        }}
        onMouseEnter={(e) => {
          e.stopPropagation();
          setDisableDragNnDropTooltip((prev) => (prev ? prev : true));
        }}
        onMouseLeave={(e) => {
          e.stopPropagation();
          setDisableDragNnDropTooltip((prev) => (!prev ? prev : false));
        }}
      >
        {controlsContent}
      </div>
    );

    return (
      <>
        <div ref={triggerRef} className="absolute -right-28 w-28 h-10" />
        {createPortal(portalContent, document.body)}
      </>
    );
  },
);

PeriodNavigationActions.displayName = 'PeriodNavigationActions';
